import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import type {
  Encounter,
  EncounterListItem,
} from "@elation/api-client/src/stitch-web";
import {
  getListEncountersByPatientQueryKey,
  listEncountersByPatient,
  useCreateEncounter,
  useGetPatientById,
} from "@elation/api-client/src/stitch-web";
import type { Patient } from "@elation/api-client/src/stitch-web/models";
import {
  isErrorStep,
  isFinishedStep,
  isFinishingStep,
  isPausedStep,
  isRecordingActiveStep,
  isScribeSocketLiveStep,
} from "@elation/transcribe";

import { AgendaV2 } from "@src/features/agenda";
import { PatientCareGapDocumentDropzone } from "@src/features/care-protocol-opportunities/v2";
import {
  clearEncounterFinishRequest,
  EncounterEntity,
  type EncounterStatus,
  markEncounterFinishHandedOff,
  requestEncounterFinish,
  useEncounterId,
  useEncounterStatusValue,
  useIsEncounterFinishHandedOff,
  useVisitRecordingSession,
  type VisitRecordingV2,
} from "@src/features/encounters";
import {
  useVisitTranscriptItems,
  VisitGeneratingNote,
  VisitRecordingBanner,
  VisitScribeSettings,
  VisitTranscript,
} from "@src/features/encounters/v2";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@src/shared/components/ui/alert";
import { Button } from "@src/shared/components/ui/button";
import { Skeleton } from "@src/shared/components/ui/skeleton";
import { Spinner } from "@src/shared/components/ui/spinner";
import { navigationV2 } from "@src/shared/navigation/v2";
import { getServerErrorMessage } from "@src/shared/utils/http/errors";
import { captureException } from "@src/shared/utils/observability/sentry";

import {
  type PatientDetailLocationState,
  resolvePatientDetailBackTarget,
  useLeaveRecordingGuard,
} from "../../../../application";
import {
  LeaveRecordingDialog,
  PatientDetailHeaderV2,
  PatientDetailTabsV2,
  type PatientDetailTabV2,
  PatientPastVisitsV2,
  toPatientPastVisitRow,
} from "../../../components/v2";

const PATIENT_ENCOUNTERS_LIMIT = 20;
const PATIENT_ENCOUNTERS_STALE_TIME_MS = 5 * 60 * 1000;
const PATIENT_ENCOUNTER_PAGE_GC_TIME_MS = 5 * 60 * 1000;
const PATIENT_ENCOUNTERS_PARAMS = {
  limit: PATIENT_ENCOUNTERS_LIMIT,
  sortBy: "startedAt",
  order: "desc",
} as const;

interface AccumulatedEncounterHistory {
  patientId: number;
  firstPageUpdatedAt: number;
  encounters: EncounterListItem[];
  nextCursor: string | null;
}

export type PatientDetailFinishLifecyclePhase = Extract<
  EncounterStatus,
  "finish_requested" | "finalizing" | "completed_awaiting_note"
>;

export interface PatientDetailPageV2Props {
  patientId: number | null;
  activeTab?: PatientDetailTabV2;
  /**
   * Injected recording for Storybook. When provided, the live scribe session
   * hook is not mounted.
   */
  recording?: VisitRecordingV2;
  scribingSessionId?: string | null;
  /**
   * Explicit Storybook phase override associated by the caller with the active
   * encounter. `undefined` uses the keyed encounter actor; `null` forces inactive.
   */
  finishLifecyclePhase?: PatientDetailFinishLifecyclePhase | null;
}

function PatientDetailSkeleton() {
  return (
    <VerticalFlexbox
      className="mx-auto min-h-0 w-full max-w-[860px] flex-1 bg-background"
      aria-busy="true"
    >
      <HorizontalFlexbox
        alignItems="center"
        justifyContent="space-between"
        className="gap-3 border-b border-border px-[18px] py-3.5"
      >
        <HorizontalFlexbox alignItems="center" className="min-w-0 gap-2.5">
          <Skeleton className="h-8 w-16 rounded-md" />
          <VerticalFlexbox className="gap-1.5">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-3 w-28" />
          </VerticalFlexbox>
        </HorizontalFlexbox>
        <Skeleton className="h-8 w-28 rounded-[9px]" />
      </HorizontalFlexbox>
      <HorizontalFlexbox className="gap-1.5 border-b border-border px-[18px] py-2">
        <Skeleton className="h-8 w-20 rounded-[7px]" />
        <Skeleton className="h-8 w-28 rounded-[7px]" />
      </HorizontalFlexbox>
      <VerticalFlexbox className="gap-3 px-[18px] py-4">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-40 rounded-card" />
      </VerticalFlexbox>
    </VerticalFlexbox>
  );
}

function PatientDetailError({ message }: { message?: string | null }) {
  return (
    <VerticalFlexbox className="mx-auto min-h-0 w-full max-w-[860px] flex-1 bg-background px-[18px] py-4">
      <Alert
        variant="destructive"
        className="rounded-card border-destructive/40 bg-destructive/5 p-card"
      >
        <AlertCircle className="size-4" aria-hidden="true" />
        <AlertTitle>Unable to load patient</AlertTitle>
        <AlertDescription>
          {message ?? "Something went wrong retrieving this patient."}
        </AlertDescription>
      </Alert>
    </VerticalFlexbox>
  );
}

interface PatientDetailViewProps {
  patientId: number | null;
  activeTab: PatientDetailTabV2;
  patient: Patient | null | undefined;
  activeEncounter: Encounter | null;
  pastVisitRows: ReturnType<typeof toPatientPastVisitRow>[];
  encountersError: unknown;
  isEncountersError: boolean;
  isEncountersLoading: boolean;
  hasMoreEncounters: boolean;
  isLoadingMoreEncounters: boolean;
  isLoadMoreEncountersError: boolean;
  isVisitActionPending: boolean;
  ensureEncounterError: string | null;
  visitActionError: string | null;
  recording: VisitRecordingV2;
  scribingSessionId: string | null;
  isRecordingLive: boolean;
  finishPhase: PatientDetailFinishLifecyclePhase | null;
  finishHandedOff: boolean;
  onStartOrResume: () => void;
  onFinishVisit: () => void;
  onResumeInProgress: () => void;
  onLoadMoreEncounters: () => void;
  onBack: () => void;
}

function PatientDetailView({
  patientId,
  activeTab,
  patient,
  activeEncounter,
  pastVisitRows,
  encountersError,
  isEncountersError,
  isEncountersLoading,
  hasMoreEncounters,
  isLoadingMoreEncounters,
  isLoadMoreEncountersError,
  isVisitActionPending,
  ensureEncounterError,
  visitActionError,
  recording,
  scribingSessionId,
  isRecordingLive,
  finishPhase,
  finishHandedOff,
  onStartOrResume,
  onFinishVisit,
  onResumeInProgress,
  onLoadMoreEncounters,
  onBack,
}: PatientDetailViewProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isFinishingVisit =
    !finishHandedOff &&
    (finishPhase != null || isFinishingStep(recording.step));
  const {
    isLeaveDialogOpen,
    requestLeave,
    keepRecording,
    allowNextNavigation,
  } = useLeaveRecordingGuard({
    isRecordingLive: isRecordingLive || isFinishingVisit,
  });

  const showLiveShell =
    isRecordingActiveStep(recording.step) ||
    isPausedStep(recording.step) ||
    isFinishingVisit ||
    isScribeSocketLiveStep(recording.step);

  // Polls continuously (not gated on the dock's transcript panel being
  // open) so the dock strip's "Transcript (N lines)" toggle stays live even
  // while collapsed, matching the pre-dock behavior where the transcript's
  // own trigger count updated regardless of its collapsed state.
  const { items: transcriptItems } = useVisitTranscriptItems({
    encounterId: activeEncounter?.id,
    scribingSessionId,
  });
  const transcriptSlot = useMemo(
    () =>
      showLiveShell ? <VisitTranscript items={transcriptItems} /> : undefined,
    [showLiveShell, transcriptItems],
  );

  const visitAction = showLiveShell
    ? null
    : (EncounterEntity.patientChromeVisitAction(activeEncounter) ??
      (activeEncounter == null ? "start" : null));

  const navigateToVisitOutput = useCallback(() => {
    if (patientId == null || activeEncounter == null) {
      toast.error("Unable to finish the visit. Please try again.");
      return;
    }

    allowNextNavigation();
    markEncounterFinishHandedOff(activeEncounter.id);
    navigate(navigationV2.visit(activeEncounter.id), {
      state: {
        source: "v2 patient detail finish",
        patientId,
        from: navigationV2.patientVisits(String(patientId)),
      },
    });
  }, [activeEncounter, allowNextNavigation, navigate, patientId]);

  useEffect(() => {
    if (finishHandedOff || finishPhase == null) return;

    const serverFinalizationBegun =
      finishPhase === "finalizing" || finishPhase === "completed_awaiting_note";

    if (isErrorStep(recording.step) && !serverFinalizationBegun) {
      if (activeEncounter != null) {
        clearEncounterFinishRequest(activeEncounter.id);
      }
      toast.error(
        recording.errorMessage ??
          "Couldn't finish the visit. Please try again.",
      );
      return;
    }

    if (finishPhase === "finish_requested" && !isFinishedStep(recording.step)) {
      return;
    }

    navigateToVisitOutput();
  }, [
    activeEncounter,
    finishHandedOff,
    finishPhase,
    navigateToVisitOutput,
    recording.errorMessage,
    recording.step,
  ]);

  const beginFinishVisit = () => {
    if (patientId == null || activeEncounter == null) {
      toast.error("Unable to finish the visit. Please try again.");
      return;
    }

    requestEncounterFinish(
      activeEncounter.id,
      activeEncounter.completedAt ?? null,
    );
    onFinishVisit();
  };

  const liveRecording: VisitRecordingV2 = {
    ...recording,
    onStop: beginFinishVisit,
  };

  const goBack = () => {
    requestLeave(onBack);
  };

  const onDialogFinish = () => {
    keepRecording();
    beginFinishVisit();
  };

  return (
    <PatientCareGapDocumentDropzone
      className="mx-auto flex min-h-0 w-full max-w-[860px] flex-1 flex-col bg-background"
      contentClassName="flex min-h-0 flex-1 flex-col"
      encounterId={activeEncounter?.id}
      patientId={patientId}
    >
      {(gapListUploads) => (
        <>
          <PatientDetailHeaderV2
            patient={patient}
            visitAction={visitAction}
            isVisitActionPending={isVisitActionPending}
            trailingActions={
              recording.isSupported !== false ? <VisitScribeSettings /> : null
            }
            onBack={goBack}
            onVisitAction={onStartOrResume}
          />

          {visitActionError ? (
            <Alert
              variant="destructive"
              className="mx-[18px] mt-3 rounded-card border-destructive/40 bg-destructive/5 md-stitch:mx-6"
            >
              <AlertCircle className="size-4" aria-hidden="true" />
              <AlertTitle>Unable to open visit</AlertTitle>
              <AlertDescription>{visitActionError}</AlertDescription>
            </Alert>
          ) : null}

          {patientId != null ? (
            <PatientDetailTabsV2 patientId={patientId} activeTab={activeTab} />
          ) : null}

          {showLiveShell && !isFinishingVisit ? (
            <div className="mx-[18px] mt-2 md-stitch:mx-6">
              <VisitRecordingBanner
                recording={liveRecording}
                stopActionLabel="Finish visit"
              />
            </div>
          ) : null}

          <main className="flex min-h-0 flex-1 flex-col">
            {isFinishingVisit ? (
              <VisitGeneratingNote />
            ) : activeTab === "agenda" ? (
              activeEncounter?.id != null ? (
                <AgendaV2
                  encounterId={activeEncounter.id}
                  gapListUploads={gapListUploads}
                  patientId={patientId ?? undefined}
                  patientFirstName={patient?.firstName ?? undefined}
                  visitStarted={showLiveShell}
                  className="px-[18px] py-3.5 md-stitch:px-6"
                  transcript={transcriptSlot}
                  transcriptLineCount={
                    showLiveShell ? transcriptItems.length : undefined
                  }
                />
              ) : ensureEncounterError ? (
                <Alert
                  variant="destructive"
                  className="m-[18px] rounded-card border-destructive/40 bg-destructive/5 md-stitch:m-6"
                >
                  <AlertCircle className="size-4" aria-hidden="true" />
                  <AlertTitle>Unable to load agenda</AlertTitle>
                  <AlertDescription>{ensureEncounterError}</AlertDescription>
                </Alert>
              ) : (
                <VerticalFlexbox
                  className="gap-3 px-[18px] py-4 md-stitch:px-6"
                  aria-busy="true"
                >
                  <Skeleton className="h-5 w-40" />
                  <Skeleton className="h-4 w-72" />
                  <Skeleton className="h-36 rounded-card" />
                </VerticalFlexbox>
              )
            ) : isEncountersError && pastVisitRows.length === 0 ? (
              <Alert
                variant="destructive"
                className="m-[18px] rounded-card border-destructive/40 bg-destructive/5 md-stitch:m-6"
              >
                <AlertCircle className="size-4" aria-hidden="true" />
                <AlertTitle>Unable to load visits</AlertTitle>
                <AlertDescription>
                  {getServerErrorMessage(encountersError) ??
                    "Something went wrong retrieving this patient's visits."}
                </AlertDescription>
              </Alert>
            ) : isEncountersLoading && pastVisitRows.length === 0 ? (
              <VerticalFlexbox
                className="gap-3 px-[18px] py-4 md-stitch:px-6"
                aria-busy="true"
              >
                <Skeleton className="h-16 rounded-none" />
                <Skeleton className="h-16 rounded-none" />
              </VerticalFlexbox>
            ) : (
              <>
                <PatientPastVisitsV2
                  encounters={pastVisitRows}
                  inProgress={
                    activeEncounter &&
                    (EncounterEntity.isStarted(activeEncounter) ||
                      showLiveShell)
                      ? {
                          encounterId: activeEncounter.id,
                          paused: isPausedStep(recording.step),
                        }
                      : null
                  }
                  onResumeInProgress={() => {
                    if (patientId == null) return;
                    navigate(navigationV2.patientDetail(String(patientId)), {
                      replace: true,
                      state: location.state,
                    });
                    onResumeInProgress();
                  }}
                  onEncounterSelect={(encounterId) => {
                    if (patientId == null) return;
                    if (isRecordingLive) {
                      // Finish current visit first; do not jump mid-recording.
                      requestLeave(() => undefined);
                      return;
                    }
                    navigate(navigationV2.visit(encounterId), {
                      state: {
                        source: "v2 patient detail history",
                        patientId,
                        from: navigationV2.patientVisits(String(patientId)),
                      },
                    });
                  }}
                />
                {isLoadMoreEncountersError ? (
                  <Alert
                    variant="destructive"
                    className="m-[18px] rounded-card border-destructive/40 bg-destructive/5 md-stitch:m-6"
                  >
                    <AlertCircle className="size-4" aria-hidden="true" />
                    <AlertTitle>Unable to load more visits</AlertTitle>
                    <AlertDescription>
                      {getServerErrorMessage(encountersError) ??
                        "Something went wrong retrieving more visits."}
                    </AlertDescription>
                    <Button
                      type="button"
                      variant="outline"
                      className="mt-3"
                      disabled={isLoadingMoreEncounters}
                      onClick={onLoadMoreEncounters}
                    >
                      {isLoadingMoreEncounters ? (
                        <>
                          <Spinner aria-hidden="true" />
                          Loading more visits
                        </>
                      ) : (
                        "Try loading more visits again"
                      )}
                    </Button>
                  </Alert>
                ) : hasMoreEncounters ? (
                  <div
                    className="border-t border-border px-[18px] py-3 text-center md-stitch:px-6"
                    aria-live="polite"
                  >
                    <Button
                      type="button"
                      variant="outline"
                      disabled={isLoadingMoreEncounters}
                      onClick={onLoadMoreEncounters}
                    >
                      {isLoadingMoreEncounters ? (
                        <>
                          <Spinner aria-hidden="true" />
                          Loading more visits
                        </>
                      ) : (
                        "Load more visits"
                      )}
                    </Button>
                  </div>
                ) : null}
              </>
            )}
          </main>

          <LeaveRecordingDialog
            open={isLeaveDialogOpen}
            onKeepRecording={keepRecording}
            onFinishVisit={onDialogFinish}
          />
        </>
      )}
    </PatientCareGapDocumentDropzone>
  );
}

function PatientDetailWithRecordingSession(
  props: Omit<
    PatientDetailViewProps,
    | "recording"
    | "scribingSessionId"
    | "isRecordingLive"
    | "isVisitActionPending"
    | "onStartOrResume"
    | "onFinishVisit"
    | "onResumeInProgress"
  > & {
    createEncounterPending: boolean;
    onPinnedEncounter: (encounter: Encounter) => void;
  },
) {
  const {
    patientId,
    activeEncounter,
    onPinnedEncounter,
    onBack,
    createEncounterPending,
    finishPhase,
    ...viewProps
  } = props;
  const [isStartingVisit, setIsStartingVisit] = useState(false);

  const {
    recording,
    scribingSessionId,
    isRecordingLive,
    startVisitRecording,
    resumeVisitRecording,
    stopVisitRecording,
  } = useVisitRecordingSession({
    encounterId: activeEncounter?.id,
    patientId: patientId ?? undefined,
    onEncounterUpdated: onPinnedEncounter,
  });

  const startOrResume = useCallback(() => {
    setIsStartingVisit(true);
    const start = isPausedStep(recording.step)
      ? resumeVisitRecording()
      : startVisitRecording();

    void start.finally(() => {
      setIsStartingVisit(false);
    });
  }, [recording.step, resumeVisitRecording, startVisitRecording]);

  return (
    <PatientDetailView
      {...viewProps}
      patientId={patientId}
      activeEncounter={activeEncounter}
      isVisitActionPending={
        isStartingVisit || (createEncounterPending && activeEncounter == null)
      }
      recording={recording}
      scribingSessionId={scribingSessionId}
      isRecordingLive={isRecordingLive}
      finishPhase={finishPhase}
      onStartOrResume={startOrResume}
      onFinishVisit={stopVisitRecording}
      onResumeInProgress={() => {
        void resumeVisitRecording();
      }}
      onBack={onBack}
    />
  );
}

export function PatientDetailPageV2({
  patientId,
  activeTab = "agenda",
  recording: recordingProp,
  scribingSessionId: scribingSessionIdProp = null,
  finishLifecyclePhase: finishLifecyclePhaseProp,
}: PatientDetailPageV2Props) {
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const queryClient = useQueryClient();
  const [visitActionError, setVisitActionError] = useState<string | null>(null);
  const [ensureEncounterError, setEnsureEncounterError] = useState<
    string | null
  >(null);
  const [accumulatedEncounterHistory, setAccumulatedEncounterHistory] =
    useState<AccumulatedEncounterHistory | null>(null);
  const [loadMoreEncountersError, setLoadMoreEncountersError] =
    useState<unknown>(null);
  const [isLoadingMoreEncounters, setIsLoadingMoreEncounters] = useState(false);
  const createEncounter = useCreateEncounter();
  const [pinnedEncounter, setPinnedEncounter] = useState<Encounter | null>(
    null,
  );
  const isInvalidPatientId = patientId == null;
  const ensureEncounterStarted = useRef(false);
  const isLoadingMoreEncountersRef = useRef(false);
  const encounterHistoryGeneration = useRef(0);
  const activeLoadMoreQuery = useRef<{
    generation: number;
    queryKey: ReturnType<typeof getListEncountersByPatientQueryKey>;
  } | null>(null);
  const previousPatientId = useRef(patientId);

  // The render-phase form this rule asks for (useState + `prev`-prop compare)
  // was tried and reverted. Clearing `ensureEncounterStarted` during a render
  // pass that React then discards makes the find-or-create-encounter effect
  // below intermittently miss its window for the new patient, so the agenda
  // never loads: the `Patient Navigation Resets Encounter State` story failed
  // ~2 runs in 5 with that form and 0 in 4 with this one. The cost of staying
  // in an effect is one frame showing the previous patient's error banner,
  // which is strictly the smaller problem.
  useEffect(() => {
    if (previousPatientId.current === patientId) return;

    previousPatientId.current = patientId;
    // react-doctor-disable-next-line react-doctor/no-adjust-state-on-prop-change
    setVisitActionError(null);
    // react-doctor-disable-next-line react-doctor/no-adjust-state-on-prop-change
    setEnsureEncounterError(null);
    // react-doctor-disable-next-line react-doctor/no-adjust-state-on-prop-change
    setPinnedEncounter(null);
    ensureEncounterStarted.current = false;
  }, [patientId]);

  const patientQuery = useGetPatientById(patientId ?? 0, {
    query: { enabled: !isInvalidPatientId, retry: false },
  });
  const encountersQueryKey = getListEncountersByPatientQueryKey(
    patientId ?? 0,
    PATIENT_ENCOUNTERS_PARAMS,
  );
  const encountersQuery = useQuery({
    queryKey: encountersQueryKey,
    queryFn: ({ signal }) =>
      listEncountersByPatient(
        patientId ?? 0,
        PATIENT_ENCOUNTERS_PARAMS,
        undefined,
        signal,
      ),
    enabled: !isInvalidPatientId,
    retry: false,
    staleTime: PATIENT_ENCOUNTERS_STALE_TIME_MS,
    refetchOnMount: true,
  });

  // These resets cannot move to render-phase adjustment. They are the tail of
  // an effect that also bumps `encounterHistoryGeneration` and cancels the
  // in-flight load-more query -- both genuine side effects that belong in an
  // effect, and the generation bump is not idempotent, so a StrictMode double
  // render would desync it from the query lifecycle. Splitting only the two
  // setState calls out would also open a window where
  // `isLoadingMoreEncountersRef` and `isLoadingMoreEncounters` disagree.
  useEffect(() => {
    encounterHistoryGeneration.current += 1;
    const activeQuery = activeLoadMoreQuery.current;
    activeLoadMoreQuery.current = null;
    if (activeQuery != null) {
      void queryClient.cancelQueries({
        queryKey: activeQuery.queryKey,
        exact: true,
      });
    }
    setAccumulatedEncounterHistory((current) =>
      current?.patientId === patientId &&
      current.firstPageUpdatedAt === encountersQuery.dataUpdatedAt
        ? current
        : null,
    );
    // react-doctor-disable-next-line react-doctor/no-adjust-state-on-prop-change
    setLoadMoreEncountersError(null);
    isLoadingMoreEncountersRef.current = false;
    // react-doctor-disable-next-line react-doctor/no-adjust-state-on-prop-change
    setIsLoadingMoreEncounters(false);
  }, [encountersQuery.dataUpdatedAt, patientId, queryClient]);

  const currentAccumulatedHistory =
    accumulatedEncounterHistory?.patientId === patientId &&
    accumulatedEncounterHistory.firstPageUpdatedAt ===
      encountersQuery.dataUpdatedAt
      ? accumulatedEncounterHistory
      : null;
  const nextEncountersCursor =
    currentAccumulatedHistory != null
      ? currentAccumulatedHistory.nextCursor
      : (encountersQuery.data?.data.nextCursor ?? null);

  const loadMoreEncounters = useCallback(() => {
    if (
      patientId == null ||
      nextEncountersCursor == null ||
      isLoadingMoreEncountersRef.current
    ) {
      return;
    }

    const cursor = nextEncountersCursor;
    const firstPageUpdatedAt = encountersQuery.dataUpdatedAt;
    const generation = encounterHistoryGeneration.current;
    const loadMoreQueryKey = getListEncountersByPatientQueryKey(patientId, {
      ...PATIENT_ENCOUNTERS_PARAMS,
      cursor,
    });
    activeLoadMoreQuery.current = {
      generation,
      queryKey: loadMoreQueryKey,
    };
    isLoadingMoreEncountersRef.current = true;
    setIsLoadingMoreEncounters(true);
    setLoadMoreEncountersError(null);

    void queryClient
      .fetchQuery({
        queryKey: loadMoreQueryKey,
        queryFn: ({ signal }) =>
          listEncountersByPatient(
            patientId,
            { ...PATIENT_ENCOUNTERS_PARAMS, cursor },
            undefined,
            signal,
          ),
        retry: false,
        staleTime: PATIENT_ENCOUNTERS_STALE_TIME_MS,
        gcTime: PATIENT_ENCOUNTER_PAGE_GC_TIME_MS,
      })
      .then((result) => {
        if (
          encounterHistoryGeneration.current !== generation ||
          queryClient.getQueryState(encountersQueryKey)?.dataUpdatedAt !==
            firstPageUpdatedAt
        ) {
          return;
        }

        setAccumulatedEncounterHistory((current) => {
          const encountersById = new Map<number, EncounterListItem>();
          const currentEncounters =
            current?.patientId === patientId &&
            current.firstPageUpdatedAt === firstPageUpdatedAt
              ? current.encounters
              : [];

          for (const encounter of currentEncounters) {
            encountersById.set(encounter.id, encounter);
          }
          for (const encounter of result.data.items) {
            if (!encountersById.has(encounter.id)) {
              encountersById.set(encounter.id, encounter);
            }
          }

          return {
            patientId,
            firstPageUpdatedAt,
            encounters: [...encountersById.values()],
            nextCursor: result.data.nextCursor ?? null,
          };
        });
      })
      .catch((error) => {
        if (encounterHistoryGeneration.current === generation) {
          setLoadMoreEncountersError(error);
        }
      })
      .finally(() => {
        if (activeLoadMoreQuery.current?.generation === generation) {
          activeLoadMoreQuery.current = null;
        }
        if (encounterHistoryGeneration.current === generation) {
          isLoadingMoreEncountersRef.current = false;
          setIsLoadingMoreEncounters(false);
        }
      });
  }, [
    encountersQuery.dataUpdatedAt,
    encountersQueryKey,
    nextEncountersCursor,
    patientId,
    queryClient,
  ]);
  const activeEncounter =
    pinnedEncounter ??
    (createEncounter.data?.data as Encounter | undefined) ??
    null;
  const actorEncounterId = useEncounterId();
  const encounterStatus = useEncounterStatusValue();
  const sameEncounter =
    activeEncounter?.id != null && actorEncounterId === activeEncounter.id;
  const actorFinishPhase: PatientDetailFinishLifecyclePhase | null =
    sameEncounter &&
    (encounterStatus === "finish_requested" ||
      encounterStatus === "finalizing" ||
      encounterStatus === "completed_awaiting_note")
      ? encounterStatus
      : null;
  const finishPhase =
    finishLifecyclePhaseProp !== undefined
      ? finishLifecyclePhaseProp
      : actorFinishPhase;
  const finishHandedOff = useIsEncounterFinishHandedOff();

  // Not prop-syncing: this effect fires a mutation, and these two clear the
  // previous attempt's error banner and pinned encounter before the retry goes
  // out. The `.catch` below clears `ensureEncounterStarted`, so this body can
  // re-enter with a stale error on screen -- dropping the resets would leave
  // that banner up while the retry is in flight.
  useEffect(() => {
    if (isInvalidPatientId || patientId == null) return;
    if (ensureEncounterStarted.current) return;

    ensureEncounterStarted.current = true;
    // react-doctor-disable-next-line react-doctor/no-adjust-state-on-prop-change
    setEnsureEncounterError(null);
    // react-doctor-disable-next-line react-doctor/no-adjust-state-on-prop-change
    setPinnedEncounter(null);
    createEncounter.reset();

    void createEncounter
      .mutateAsync({ data: { patientId } })
      .then((result) => {
        setPinnedEncounter(result.data);
      })
      .catch((error) => {
        ensureEncounterStarted.current = false;
        captureException(error, {
          tags: { feature: "patients-v2", action: "find-or-create-encounter" },
          extra: { patientId },
        });
        setEnsureEncounterError(
          getServerErrorMessage(error) ??
            "Unable to prepare today's visit agenda. Please try again.",
        );
      });
  }, [
    createEncounter.mutateAsync,
    createEncounter.reset,
    isInvalidPatientId,
    patientId,
  ]);

  const pastVisitRows = useMemo(() => {
    const encountersById = new Map<number, EncounterListItem>();

    for (const encounter of encountersQuery.data?.data.items ?? []) {
      encountersById.set(encounter.id, encounter);
    }
    for (const encounter of currentAccumulatedHistory?.encounters ?? []) {
      if (!encountersById.has(encounter.id)) {
        encountersById.set(encounter.id, encounter);
      }
    }

    return [...encountersById.values()].map((encounter) =>
      toPatientPastVisitRow(encounter),
    );
  }, [currentAccumulatedHistory?.encounters, encountersQuery.data?.data.items]);

  const resolveBackTarget = useCallback(() => {
    const historyState = window.history.state as { idx?: number } | null;
    const target = resolvePatientDetailBackTarget({
      state: locationState as PatientDetailLocationState | null,
      historyIndex: historyState?.idx,
      fallbackPath: navigationV2.patients(),
    });

    if (target.kind === "history") {
      navigate(-1);
      return;
    }

    navigate(target.to);
  }, [locationState, navigate]);

  if (!isInvalidPatientId && patientQuery.isLoading) {
    return <PatientDetailSkeleton />;
  }

  if (isInvalidPatientId || patientQuery.isError) {
    return (
      <PatientDetailError
        message={
          isInvalidPatientId
            ? "The patient link is invalid."
            : (getServerErrorMessage(patientQuery.error) ?? null)
        }
      />
    );
  }

  const createEncounterPending = createEncounter.isPending;
  const sharedViewProps = {
    patientId,
    activeTab,
    patient: patientQuery.data?.data,
    activeEncounter,
    pastVisitRows,
    encountersError: loadMoreEncountersError ?? encountersQuery.error,
    isEncountersError: encountersQuery.isError,
    isEncountersLoading: encountersQuery.isLoading,
    hasMoreEncounters: nextEncountersCursor != null,
    isLoadingMoreEncounters,
    isLoadMoreEncountersError: loadMoreEncountersError != null,
    ensureEncounterError,
    visitActionError,
    finishPhase,
    finishHandedOff,
    onLoadMoreEncounters: loadMoreEncounters,
    onBack: resolveBackTarget,
  };

  if (recordingProp != null) {
    return (
      <PatientDetailView
        {...sharedViewProps}
        recording={recordingProp}
        scribingSessionId={scribingSessionIdProp}
        isRecordingLive={isScribeSocketLiveStep(recordingProp.step)}
        isVisitActionPending={createEncounterPending && activeEncounter == null}
        onStartOrResume={() => {
          setVisitActionError(null);
          if (isPausedStep(recordingProp.step)) {
            recordingProp.onResume();
            return;
          }
          recordingProp.onStart();
        }}
        onFinishVisit={() => {
          recordingProp.onStop();
        }}
        onResumeInProgress={() => {
          recordingProp.onResume();
        }}
      />
    );
  }

  return (
    <PatientDetailWithRecordingSession
      {...sharedViewProps}
      createEncounterPending={createEncounterPending}
      onPinnedEncounter={(encounter) => {
        setPinnedEncounter(encounter);
      }}
    />
  );
}
